import { Box, Button, Card, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, SelectChangeEvent, Table, TableBody, TablePagination, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react';
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';
import { Close, Search } from '@mui/icons-material';
import { HeadCell, Order } from '../../../../types/table';
import { EnhancedTableHead } from '../../../../components/Table/EnhancedTableHead';
import TestGroupTableListItems from './TestGroupTableListItems';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import defaultLumns from '../../../../assets/images/default_luminaire_image.png';
import { defaultconfigureLumnListData, WhitelistZoneData } from '../../../../state/assets-state';
import { ConfigureLuminaryDetailsList } from '../../../../types/configure-luminaries-list-types';
import { useSelector } from 'react-redux';
import { Selectors } from '../../../../selectors';

type Props = {
    deviceWhitelistZone: WhitelistZoneData | null,
    currentLumnCount: number,
    othersLumnCount: number,
    testGroupId: number | null,
    searchQuery: string,
    onSearchChange: (query: string) => void,
    rowsPerPage: number,
    setRowsPerPage: (rowsPerPage: number) => void,
    offset: number,
    setOffset: (offset: number) => void,
    currentPage: number,
    setCurrentPage: (page: number) => void
}
const TestGroupTable: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const { t } = useLanguageLocale();
    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const [localSearchQuery, setLocalSearchQuery] = useState(props.searchQuery);
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState('ZonesName');
    const [totalRecords, setTotalRecords] = React.useState(10);
    const [selected, setSelected] = React.useState<string[]>([]);
    const [selectedGroup, setSelectedGroup] = React.useState("");
    const [openTransferDialog, setOpenTransferDialog] = React.useState(false);
    const asset = useSelector(Selectors.Asset.assets);
      const [configureLumnListData, setConfigureLumnListData] = useState<ConfigureLuminaryDetailsList>(defaultconfigureLumnListData);
    const allLuminaires = asset.configureLuminariesListDetails?.luminaryConfigs || [];

    // Debounce search - wait 3 seconds after user stops typing
    useEffect(() => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout to call parent after 3 seconds
        searchTimeoutRef.current = setTimeout(() => {
            props.onSearchChange(localSearchQuery);
        }, 2000);

        // Cleanup on unmount or when localSearchQuery changes
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localSearchQuery]);

    // ✅ Filter luminaires based on testGroupId
    const filteredLuminaires = props.testGroupId === null
        ? allLuminaires // Show all luminaires for "All" tab
        : allLuminaires.filter(lum => lum.testGroupId === props.testGroupId); // Filter by test group

    const zonesList = [
        { id: 1, zones: "1. Entry", lumnName: "Lum Name", epas: "16025", productFamily: "Velos LED", category: "Exit Sign", partNo: "123456789", testGroup: "1", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 2, zones: "1. Entry", lumnName: "Lum Name", epas: "16025", productFamily: "Cube-y Tech", category: "Exit Sign", partNo: "123456789", testGroup: "2", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 3, zones: "1. Entry", lumnName: "Lum Name", epas: "16025", productFamily: "Velos LED", category: "Exit Sign", partNo: "123456789", testGroup: "3", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 4, zones: "2. Outpatient Care", lumnName: "Lum Name", epas: "16025", productFamily: "NexiTech IA", category: "Exit Sign", partNo: "123456789", testGroup: "4", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 5, zones: "2. Outpatient Care", lumnName: "Lum Name", epas: "16025", productFamily: "Velos LED", category: "Exit Sign", partNo: "123456789", testGroup: "1", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 6, zones: "2. Outpatient Care", lumnName: "Lum Name", epas: "16025", productFamily: "NexiTech IA", category: "Exit Sign", partNo: "123456789", testGroup: "2", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 7, zones: "3. Emergency Care", lumnName: "Lum Name", epas: "16025", productFamily: "Cube-y Tech", category: "Exit Sign", partNo: "123456789", testGroup: "3", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 8, zones: "3. Emergency Care", lumnName: "Lum Name", epas: "16025", productFamily: "NexiTech IA", category: "Exit Sign", partNo: "123456789", testGroup: "4", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 9, zones: "3. Emergency Care", lumnName: "Lum Name", epas: "16025", productFamily: "Cube-y Tech", category: "Exit Sign", partNo: "123456789", testGroup: "1", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
        { id: 10, zones: "1. Entry", lumnName: "Lum Name", epas: "16025", productFamily: "Velos LED", category: "Exit Sign", partNo: "123456789", testGroup: "2", ratedDuration: "90 minutes", optionalDescription: "South side, by the printers" },
    ];

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = filteredLuminaires.map((n) => n.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleSelect = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const headCells: HeadCell[] = [
        {
            id: 'selectAll',
            label: (
                <Checkbox
                    color="primary"
                    sx={{
                        p: 0,
                        marginLeft: '-57%',
                    }}
                    indeterminate={selected.length > 0 && selected.length < filteredLuminaires.length}
                    checked={filteredLuminaires.length > 0 && selected.length === filteredLuminaires.length}
                    onChange={handleSelectAllClick}
                    inputProps={{
                        'aria-label': 'select all items',
                    }}
                />
            ),
            hideSorting: true
        },
        { id: 'zone', label: t('DEVICE_DETAILS.ZONES'), hideSorting: true },
        { id: 'name', label: t('DEVICE_DETAILS.NAME'), hideSorting: true },
        { id: 'epas', label: t('ASSETS_DETAILS.EPAS'), hideSorting: true },
        { id: 'productFamily', label: t('ASSETS_DETAILS.PRODUCT_FAMILY'), hideSorting: true },
        { id: 'category', label: t('ASSETS_DETAILS.CATEGORY'), hideSorting: true },
        { id: 'partNo', label: t('DEVICE_DETAILS.PART_NUMBER'), hideSorting: true },
        { id: 'testGroup', label: t('DEVICE_DETAILS.TEST_GROUP'), hideSorting: true },
        { id: 'ratedDuration', label: t('ASSETS_DETAILS.RATED_DURATION'), hideSorting: true },
        { id: 'optionalDescription', label: t('ASSETS_DETAILS.OPTIONAL_DESCRIPTION'), hideSorting: true },
    ];

    const groupList = [
        { id: "1", name: "Group 1" },
        { id: "2", name: "Group 2" },
        { id: "3", name: "Group 3" },
        { id: "4", name: "Group 4" },
    ]

    const transferLumnsList = [
        { id: '1', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '2', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '3', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '4', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '5', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '6', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '7', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '8', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '9', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
        { id: '10', name: 'Blinky', desc: 'Exit Sign,VELOS LED', info: 'Info' },
    ]
    const handleGroupList = (event: SelectChangeEvent) => {
        setSelectedGroup(event.target.value);
    }

    const onChangeSearchText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setLocalSearchQuery(e.target.value);
    };

    const resetSearch = (): void => {
        setLocalSearchQuery('');
        props.onSearchChange(''); // Immediately clear search in parent
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: any): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        props.setCurrentPage(newPage);
        const newOffset = newPage * props.rowsPerPage + 1;
        props.setOffset(newOffset);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        props.setRowsPerPage(newRowsPerPage);
        props.setCurrentPage(0);
        props.setOffset(1);
    };

    const handleTransfer = () => {
        setOpenTransferDialog(true);
    };

    const handleCloseCommentDialog = () => {
        setOpenTransferDialog(false);
    }

    const onCommentsSave = () => {
        setOpenTransferDialog(false);
    }

    useEffect(() => {
            if (asset.configureLuminariesListLoading) {

                return;
            }
            if (asset.configureLuminariesListSuccess) {
                setConfigureLumnListData(asset.configureLuminariesListDetails);
                setTotalRecords(asset.configureLuminariesListDetails.totalRecordCount);

            } else if (asset.configureLuminariesListFail) {
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [asset.configureLuminariesListLoading, asset.configureLuminariesListSuccess, asset.configureLuminariesListFail, asset.configureLuminariesListDetails]);

    return (
        <>
            <div data-testid="test-group-table">
                <Box>
                    <Box sx={{ display: "flex", gap: "2%", fontSize: "14" }}>
                        <Typography data-testid="current-luminaires-count"><strong>{props.currentLumnCount} </strong>{t('ASSETS_DETAILS.LUMINAIRES_ASSIGNED')}</Typography>
                        <Typography data-testid="other-luminaires-count"><strong>{props.othersLumnCount} </strong>{t('ASSETS_DETAILS.LUMINAIRES_ASSIGNED_OTHER')}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: md ? '43% 2fr' : '', gap: '1%', gridTemplateRows: md ? '' : 'auto auto' }}>
                        <Box sx={{ display: 'flex', gap: '5%', marginTop: '2%', marginBottom: '1%', }}>
                            <TextField
                                inputProps={{ 'data-testid': 'searchText-field', maxLength: 100 }}
                                value={localSearchQuery}
                                style={{
                                    width: md ? '50%' : 'auto',
                                    borderRadius: 5,
                                    backgroundColor: PXBColors.white[50],
                                    fontSize: 12,
                                    fontWeight: 400,
                                }}
                                placeholder={t('ORGANIZATION_MANAGE.SEARCH_BUILDINGS')}
                                onChange={(e): void => onChangeSearchText(e)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search data-testid="search-icon" style={{ color: PXBColors.gray[500] }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {localSearchQuery !== '' && (
                                                <IconButton onClick={resetSearch}>
                                                    <Close data-testid="close-search-icon" fontSize="small" />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                            />
                            {/* <Typography data-testid="transfer-luminaires-text" fontSize={16} marginTop={1}>
                                {t('ACTIONS.TRANSFER')} <strong>12 Luminaires</strong> To
                            </Typography> */}
                        </Box>
                        {/* <Box sx={{ display: 'flex', gap: '5%', marginTop: '2%', marginBottom: '1%', }}>
                            <Select
                                id="group-list"
                                data-testid="group-list"
                                style={{
                                    width: md ? '30%' : 'auto',
                                    height: 36,
                                    borderBlock: 0,
                                    outline: 'none',
                                    overflow: 'hidden',
                                    fontSize: 16,
                                    color: PXBColors.black[500],
                                    fontStyle: 'normal',
                                    borderRadius: 5,
                                    fontWeight: 400,
                                    backgroundColor: PXBColors.white[50],
                                }}
                                displayEmpty
                                value={selectedGroup}
                                onChange={handleGroupList}
                            >
                                <MenuItem value=""
                                    style={selectStyle.dropdown}> {t('ASSETS_DETAILS.ALL_GROUPS')}</MenuItem>
                                {groupList.map((data) => {
                                    return (
                                        <MenuItem key={data.id} value={data.id} style={selectStyle.dropdown}>
                                            {data.name}
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                            <Button disabled
                                data-testid="transfer-luminaires-button"
                                style={{
                                    width: 'auto',
                                    height: 36,
                                    flexDirection: 'row',
                                    backgroundColor: PXBColors.blue[200],
                                    color: PXBColors.white[50],
                                }}
                                onClick={handleTransfer}
                                variant='contained'
                                startIcon={<CompareArrowsIcon />}>
                                {t('ACTIONS.TRANSFER')}
                            </Button>
                        </Box> */}
                    </Box>


                    <Card style={{ flex: '1 1 0px', overflow: 'auto', marginTop: '1%' }} id="configuration-lumns-table" data-testid="configuration-lumns-table">
                        <Table stickyHeader size="small"
                            id="configLumn-list-table"
                            data-testid="configLumn-list-table">
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                headCells={headCells}
                            />
                            <TableBody className="tableHover" style={{ minHeight: 100 }} key={'mainTable'}
                                id="config-lumns-list-table-body"
                                data-testid="config-lumns-list-table-body">
                                {asset.configureLuminariesListLoading ? (
                                    <tr>
                                        <td colSpan={headCells.length} style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <CircularProgress />
                                        </td>
                                    </tr>
                                ) : filteredLuminaires.length === 0 ? (
                                    <tr>
                                        <td colSpan={headCells.length} style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <Typography variant="body1" color="textSecondary">
                                               {t('ORGANIZATION_MANAGE.ERROR_NO_LUMINARY_FOUND')}
                                            </Typography>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLuminaires.map((row) => (
                                        <TestGroupTableListItems
                                            key={Number(row.id)}
                                            row={row}
                                            selected={selected}
                                            onSelectRow={handleSelect}
                                        />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Card >
                    <TablePagination
                        component="div"
                        data-testid="pagination-users-service-partner"
                        count={totalRecords}
                        page={props.currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={props.rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t('LABELS.ITEMS_PER_PAGE')}
                        showFirstButton showLastButton
                    />
                </Box>
            </div>

            <Dialog id='commentDialogBox' maxWidth="sm" open={openTransferDialog} onClose={handleCloseCommentDialog} style={{ borderRadius: 4, boxShadow: '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)' }} >
                <DialogTitle sx={{ display: 'flex', }}>
                    <IconButton onClick={handleCloseCommentDialog}>
                        <CloseIcon />
                    </IconButton>
                    {t('ASSETS_DETAILS.TRANSFER_LUMINAIRES')} Group 1 to Group 3?
                </DialogTitle>
                <Divider style={{ color: 'grey' }} />
                <DialogContent id='commentBoxContent' sx={{ display: 'flex', flexDirection: 'row', height: 350 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 5, width: '100%', }}>
                        {transferLumnsList.map((data) => (
                            <Box key={data.id} sx={{ display: 'flex', }}>
                                <Checkbox />
                                <img src={defaultLumns} alt={data.name} style={{ width: 50, height: 50 }} />
                                <Box sx={{ gap: 2, marginLeft: 2 }}>
                                    <Typography variant="body2">{data.id} {data.name}</Typography>
                                    <Typography variant="body2">{data.desc}</Typography>
                                    <Typography variant="body2">{data.info}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <Divider style={{ color: 'grey' }} />
                <DialogActions>
                    <Button
                        style={{
                            marginBottom: 10,
                            width: 'auto',
                            height: 36,
                            flexDirection: 'row',
                            marginRight: 10,
                            backgroundColor: PXBColors.white[50],
                        }}
                        id="cancelButton"
                        variant="outlined"
                        color="primary"
                        data-testid="cancelButton"
                        onClick={handleCloseCommentDialog}
                        startIcon={<CloseIcon />}
                    >
                        {t('ACTIONS.CANCEL')}
                    </Button>
                    <Button
                        style={{
                            marginBottom: 10,
                            width: 'auto',
                            height: 36,
                            flexDirection: 'row',
                            backgroundColor: PXBColors.blue[500],
                            color: PXBColors.white[50],
                        }}
                        startIcon={<CheckIcon />}
                        color="primary"
                        id="saveButton"
                        data-testid="saveButton"
                        variant='contained'
                        onClick={onCommentsSave}
                    >
                        {t('ACTIONS.TRANSFER')}
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    )
}

export default TestGroupTable
